<script setup lang="ts">
import { ref } from 'vue'
import { Menu, X, Gamepad2, ChevronDown, LogIn, UserPlus } from 'lucide-vue-next'

const props = defineProps<{
  userSignedIn?: boolean
  userName?: string
  isAdmin?: boolean
  currentPath?: string
  loginPath?: string
  signupPath?: string
  logoutPath?: string
  editProfilePath?: string
}>()

const csrfToken = document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

const mobileMenuOpen = ref(false)
const userDropdownOpen = ref(false)

// 公開ページナビリンク
const navLinks = [
  { to: '/', label: 'TOP' },
  { to: '/pricing', label: '料金' },
  { to: '/how-to-use', label: '使い方' },
  { to: '/equipment', label: '設備' },
  { to: '/access', label: 'アクセス' },
  { to: '/faq', label: 'FAQ' },
  { to: '/availability', label: '予約状況' },
  { to: '/contact', label: 'お問い合わせ' },
]

// ログイン済みユーザー向け追加リンク
const authLinks = [
  { to: '/reservations', label: 'マイ予約' },
]

function isActive(path: string) {
  if (path === '/') return props.currentPath === '/'
  return props.currentPath?.startsWith(path) ?? false
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

function toggleUserDropdown() {
  userDropdownOpen.value = !userDropdownOpen.value
}

function closeUserDropdown() {
  userDropdownOpen.value = false
}

function handleDropdownFocusOut(event: FocusEvent) {
  const container = event.currentTarget as HTMLElement
  if (!container.contains(event.relatedTarget as Node)) {
    userDropdownOpen.value = false
  }
}
</script>

<template>
  <header class="fixed top-4 left-4 right-4 z-50">
    <div class="max-w-7xl mx-auto bg-white border-4 border-[#5a3a26] shadow-[8px_8px_0px_0px_#3f2618] rounded-sm">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16 md:h-20">
          <!-- ロゴ -->
          <a
            href="/"
            class="flex items-center gap-2 group relative z-10"
          >
            <div class="bg-lime-400 absolute -inset-2 skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            <!-- Viteビルド時にsrc="/images/..."だとRollupが解決しようとしてエラーになるため:srcを使用 -->
            <img
              :src="'/images/hideout/logo.png'"
              alt="HIDE OUT NAGAI"
              class="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105 group-hover:rotate-2"
            >
          </a>

          <!-- デスクトップナビ -->
          <nav class="hidden lg:flex items-center space-x-1">
            <a
              v-for="link in navLinks"
              :key="link.to"
              :href="link.to"
              class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap"
              :class="isActive(link.to)
                ? 'text-[#5a3a26] border-[#5a3a26] bg-lime-400 shadow-[2px_2px_0px_0px_#3f2618]'
                : 'text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]'"
            >
              {{ link.label }}
            </a>

            <!-- ログイン済み: 追加リンク + ユーザードロップダウン -->
            <template v-if="userSignedIn">
              <a
                v-for="link in authLinks"
                :key="link.to"
                :href="link.to"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap"
                :class="isActive(link.to)
                  ? 'text-[#5a3a26] border-[#5a3a26] bg-lime-400 shadow-[2px_2px_0px_0px_#3f2618]'
                  : 'text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]'"
              >
                {{ link.label }}
              </a>

              <a
                v-if="isAdmin"
                href="/hideout_admin"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618]"
              >
                管理画面
              </a>

              <!-- ユーザードロップダウン -->
              <div
                class="relative ml-2"
                @mouseleave="closeUserDropdown"
                @focusout="handleDropdownFocusOut"
              >
                <button
                  class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618] flex items-center gap-1"
                  aria-haspopup="true"
                  :aria-expanded="userDropdownOpen"
                  @mouseenter="userDropdownOpen = true"
                  @click="toggleUserDropdown"
                >
                  {{ userName }}様
                  <ChevronDown :size="16" />
                </button>
                <div
                  v-show="userDropdownOpen"
                  class="absolute right-0 top-full pt-2 w-48 z-10"
                >
                  <div class="bg-white border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] rounded-sm py-1">
                    <a
                      href="/reservations/new"
                      class="block px-4 py-2 text-sm font-bold text-[#5a3a26] hover:bg-lime-400"
                    >
                      新規予約
                    </a>
                    <a
                      :href="editProfilePath"
                      class="block px-4 py-2 text-sm font-bold text-[#5a3a26] hover:bg-lime-400"
                    >
                      プロフィール編集
                    </a>
                    <form
                      :action="logoutPath"
                      method="post"
                    >
                      <input
                        type="hidden"
                        name="_method"
                        value="delete"
                      >
                      <input
                        type="hidden"
                        name="authenticity_token"
                        :value="csrfToken"
                      >
                      <button
                        type="submit"
                        class="block w-full text-left px-4 py-2 text-sm font-bold text-[#5a3a26] hover:bg-lime-400"
                      >
                        ログアウト
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </template>

            <!-- 未ログイン: ログイン/新規登録 -->
            <template v-else>
              <a
                :href="loginPath"
                class="px-3 py-2 text-sm font-black tracking-wide transition-all border-2 rounded-sm uppercase whitespace-nowrap text-slate-600 border-transparent hover:border-[#5a3a26] hover:bg-lime-400 hover:shadow-[2px_2px_0px_0px_#3f2618] flex items-center gap-1"
              >
                <LogIn :size="16" />
                ログイン
              </a>
              <a
                :href="signupPath"
                class="px-3 py-3 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm text-sm whitespace-nowrap flex items-center gap-1"
              >
                <UserPlus :size="16" />
                新規登録
              </a>
            </template>
          </nav>

          <!-- モバイルメニューボタン -->
          <button
            class="lg:hidden p-2 text-[#5a3a26] hover:bg-lime-400 border-2 border-transparent hover:border-[#5a3a26] transition-all rounded-sm"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <Menu
              v-if="!mobileMenuOpen"
              :size="28"
              :stroke-width="3"
            />
            <X
              v-else
              :size="28"
              :stroke-width="3"
            />
          </button>
        </div>
      </div>

      <!-- モバイルメニュー -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="mobileMenuOpen"
          class="lg:hidden bg-white border-t-4 border-[#5a3a26] overflow-hidden"
        >
          <div class="px-4 py-4 space-y-2">
            <a
              v-for="link in navLinks"
              :key="link.to"
              :href="link.to"
              class="block px-4 py-3 border-4 text-base font-black uppercase rounded-sm transition-all"
              :class="isActive(link.to)
                ? 'bg-lime-400 text-[#5a3a26] border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618]'
                : 'text-slate-600 border-transparent hover:bg-slate-100 hover:border-slate-300'"
              @click="closeMobileMenu"
            >
              <span class="flex items-center gap-2">
                <Gamepad2
                  v-if="isActive(link.to)"
                  :size="16"
                />
                {{ link.label }}
              </span>
            </a>

            <!-- ログイン済み: 追加リンク -->
            <template v-if="userSignedIn">
              <a
                v-for="link in authLinks"
                :key="link.to"
                :href="link.to"
                class="block px-4 py-3 border-4 text-base font-black uppercase rounded-sm transition-all"
                :class="isActive(link.to)
                  ? 'bg-lime-400 text-[#5a3a26] border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618]'
                  : 'text-slate-600 border-transparent hover:bg-slate-100 hover:border-slate-300'"
                @click="closeMobileMenu"
              >
                <span class="flex items-center gap-2">
                  <Gamepad2
                    v-if="isActive(link.to)"
                    :size="16"
                  />
                  {{ link.label }}
                </span>
              </a>

              <a
                v-if="isAdmin"
                href="/hideout_admin"
                class="block px-4 py-3 border-4 text-base font-black uppercase rounded-sm transition-all text-slate-600 border-transparent hover:bg-slate-100 hover:border-slate-300"
                @click="closeMobileMenu"
              >
                <span class="flex items-center gap-2">管理画面</span>
              </a>

              <div class="pt-4 mt-4 border-t-2 border-dashed border-slate-300">
                <a
                  href="/reservations/new"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  新規予約
                </a>
                <a
                  :href="editProfilePath"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  プロフィール編集
                </a>
                <form
                  :action="logoutPath"
                  method="post"
                >
                  <input
                    type="hidden"
                    name="_method"
                    value="delete"
                  >
                  <input
                    type="hidden"
                    name="authenticity_token"
                    :value="csrfToken"
                  >
                  <button
                    type="submit"
                    class="block w-full text-center py-3 px-4 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm"
                  >
                    ログアウト
                  </button>
                </form>
              </div>
            </template>

            <!-- 未ログイン: ログイン/新規登録 -->
            <template v-else>
              <div class="pt-4 mt-4 border-t-2 border-dashed border-slate-300">
                <a
                  :href="loginPath"
                  class="block w-full text-center py-2 px-4 text-sm font-black text-[#5a3a26] hover:text-lime-600 mb-2"
                >
                  ログイン
                </a>
                <a
                  :href="signupPath"
                  class="block w-full text-center py-3 px-4 bg-[#84cc16] text-[#3f2618] font-black border-4 border-[#5a3a26] shadow-[4px_4px_0px_0px_#3f2618] active:translate-y-1 active:shadow-none transition-all uppercase tracking-widest rounded-sm"
                >
                  新規登録
                </a>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </header>
</template>
